import { createContext, forwardRef, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors, Sizes } from '@/components/ui/types';
import Fieldset from '../fieldset';
import { FloatingLabel, Label, LabelProps } from '../label';
import {
  SelectOptionProps,
  SelectProps,
  SelectVariant,
  SelectWrapperProps,
} from './types';

type SelectContextValue = {
  isInsideWrapper: boolean;
};

const SelectContext = createContext<SelectContextValue>({
  isInsideWrapper: false,
});

const useSelectContext = () => useContext(SelectContext);

const SelectValue = () => (
  <button type="button" className="flex flex-1 items-center min-w-0">
    <selectedcontent className="block min-w-0 truncate" />
  </button>
);

const sizeClasses: Record<Sizes, string> = {
  xs: 'select-xs',
  sm: 'select-sm',
  md: 'select-md',
  lg: 'select-lg',
  xl: 'select-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'select-neutral',
  primary: 'select-primary',
  secondary: 'select-secondary',
  accent: 'select-accent',
  info: 'select-info',
  success: 'select-success',
  warning: 'select-warning',
  error: 'select-error',
};

const variantClasses: Record<SelectVariant, string> = {
  default: '',
  ghost: 'select-ghost',
};

const SelectBase = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      size = 'md',
      color = 'default',
      variant = 'default',
      error = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { isInsideWrapper } = useSelectContext();

    const classes = twMerge(
      isInsideWrapper ? 'grow' : 'select',
      !isInsideWrapper && sizeClasses[size],
      !isInsideWrapper && colorClasses[color],
      !isInsideWrapper && variantClasses[variant],
      !isInsideWrapper && error && 'select-error',
      className,
    );

    return (
      <select ref={ref} className={classes} {...props}>
        {!props.multiple && <SelectValue />}
        {children}
      </select>
    );
  },
);

const Wrapper = ({
  className,
  children,
  size = 'md',
  color = 'default',
  variant = 'default',
  error = false,
  disabled = false,
  floatingLabel,
}: SelectWrapperProps) => {
  const classes = twMerge(
    'select',
    sizeClasses[size],
    colorClasses[color],
    variantClasses[variant],
    error && 'select-error',
    disabled && 'select-disabled',
    className,
  );

  const content = <label className={classes}>{children}</label>;

  if (floatingLabel) {
    return (
      <SelectContext.Provider value={{ isInsideWrapper: true }}>
        <FloatingLabel label={floatingLabel}>{content}</FloatingLabel>
      </SelectContext.Provider>
    );
  }

  return (
    <SelectContext.Provider value={{ isInsideWrapper: true }}>
      {content}
    </SelectContext.Provider>
  );
};

const Option = forwardRef<HTMLOptionElement, SelectOptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <option ref={ref} className={className} {...props}>
        {children}
      </option>
    );
  },
);

const SelectLabel = (props: LabelProps<'span'>) => (
  <Label {...props} as="span" />
);

SelectBase.displayName = 'Select';
Option.displayName = 'Select.Option';
SelectLabel.displayName = 'Select.Label';

export const Select = Object.assign(SelectBase, {
  Wrapper,
  Option,
  Label: SelectLabel,
  Fieldset,
});
